import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Waves, Clock, Phone, Star, Coffee, Car, Wifi } from "lucide-react";
import { BUSINESS_SCHEMA, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/crystal-beach-bed-and-breakfast")({
  head: () => ({
    meta: [
      { title: "Bed and Breakfast in Crystal Beach Ontario - Malak BnB Resort" },
      {
        name: "description",
        content:
          "Malak BnB Resort is a boutique bed and breakfast in Crystal Beach, Ontario. Steps from Bay Beach, Lake Erie, with breakfast, outdoor pool, and direct reservations.",
      },
      {
        property: "og:title",
        content: "Bed and Breakfast in Crystal Beach Ontario - Malak BnB Resort",
      },
      {
        property: "og:description",
        content:
          "Boutique BnB steps from Bay Beach and Lake Erie. Breakfast included, outdoor heated pool, direct reservation requests.",
      },
      { property: "og:url", content: "https://malakbnbresort.ca/crystal-beach-bed-and-breakfast" },
      { property: "og:image", content: "/images/hero-exterior.webp" },
      { "script:ld+json": BUSINESS_SCHEMA },
      { "script:ld+json": faqPageSchema(FAQ) },
    ],
    links: [canonical("/crystal-beach-bed-and-breakfast")],
  }),
  component: CrystalBeachBnBPage,
});

const NEARBY = [
  { icon: Waves, label: "Bay Beach", detail: "5-minute walk" },
  { icon: MapPin, label: "Niagara Falls", detail: "30-minute drive" },
  { icon: MapPin, label: "Fort Erie", detail: "10-minute drive" },
  { icon: MapPin, label: "Ridgeway", detail: "5-minute drive" },
  { icon: MapPin, label: "Port Colborne", detail: "25-minute drive" },
  { icon: Clock, label: "Buffalo Border", detail: "15-minute drive" },
];

const INCLUDED = [
  { icon: Coffee, label: "Continental breakfast buffet" },
  { icon: Waves, label: "Outdoor heated pool" },
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Car, label: "Free parking" },
];

const FAQ = [
  {
    q: "Where is Malak BnB Resort located?",
    a: "Malak BnB Resort is located at 264 Ridge Road South in Crystal Beach, Ontario. Crystal Beach is a small lakeside community in the Fort Erie area of the Niagara Region, roughly 30 minutes from Niagara Falls and about 15 minutes from the Peace Bridge crossing into Buffalo, New York.",
  },
  {
    q: "How close is Malak BnB Resort to Bay Beach?",
    a: "Malak BnB Resort is a 5-minute walk from Bay Beach in Crystal Beach. Bay Beach is one of the most popular sandy beaches along the Canadian side of Lake Erie, known for calm, warm water in summer and a relaxed, family-friendly atmosphere.",
  },
  {
    q: "Is breakfast included at Malak BnB Resort?",
    a: "Yes. A continental breakfast buffet is included with every stay at Malak BnB Resort. Guests can enjoy a proper morning meal before heading out to Bay Beach, exploring the town, or making the drive to Niagara Falls.",
  },
  {
    q: "Does Malak BnB Resort have a pool?",
    a: "Yes. Malak BnB Resort has an outdoor heated swimming pool, along with indoor and outdoor lounge areas for guests to relax in.",
  },
  {
    q: "How do I book a room at Malak BnB Resort?",
    a: "Malak BnB Resort takes direct reservation requests. You submit a request with your preferred room and dates, and the team personally reviews and confirms your stay. Payment details are sent after confirmation. There are no third-party booking fees.",
  },
  {
    q: "Is Malak BnB Resort close to Niagara Falls?",
    a: "Malak BnB Resort is approximately 30 minutes by car from Niagara Falls. Many guests use Crystal Beach as a quieter, more affordable base for a Niagara Falls day trip, avoiding the busy tourist core while still reaching the falls in under half an hour.",
  },
  {
    q: "What types of rooms are available at Malak BnB Resort?",
    a: "Malak BnB Resort offers four room types: 2 Double Beds (450 sq ft, from C$345), Standard Double Bed (400 sq ft, from C$345), Deluxe Queen Bed (400 sq ft, from C$355), and Queen Bed Suite (440 sq ft, from C$380). All rooms include air conditioning, flat screen TV, free Wi-Fi, and an in-room safe.",
  },
];

function CrystalBeachBnBPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/hero-exterior.webp"
          alt="Exterior of Malak BnB Resort in Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">
              Crystal Beach, Ontario
            </p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Bed and Breakfast in Crystal Beach, Ontario
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">
            A Lakeside BnB Worth Finding
          </p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Malak BnB Resort sits a five-minute walk from Bay Beach on Lake Erie, and it feels
            exactly like that sounds.
          </p>
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              There is a particular kind of morning that happens at a good bed and breakfast. You
              come downstairs to a proper breakfast already laid out, the smell of coffee already in
              the air, and the day ahead of you is somehow both wide open and completely unhurried.
              That is what Malak BnB Resort is built around.
            </p>
            <p>
              This is a family-owned property at 264 Ridge Road South in Crystal Beach, Ontario.
              Four rooms, each with hardwood floors, plush bedding, air conditioning, and everything
              you need for a comfortable stay. An outdoor heated pool for afternoons when the beach
              gets busy. Continental breakfast every morning so you start the day properly.
            </p>
            <p>
              Crystal Beach is a small lakeside town in the Niagara Region. Quiet enough to actually
              rest, but close enough to Bay Beach, the Niagara Falls, Fort Erie, and cycling routes
              that you will not run out of things to do. If you are looking for a bed and breakfast
              near Lake Erie in Ontario, this is one worth staying at.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Request to Book
            </Link>
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              View Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* Lake Erie photo interlude */}
      <section className="relative h-[380px] sm:h-[460px] overflow-hidden">
        <img
          src="/images/pexels-lake-erie-shore.webp"
          alt="Lake Erie shoreline near Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <p className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-4">
            Lake Erie
          </p>
          <p className="text-3xl sm:text-4xl font-heading text-white max-w-xl leading-tight">
            The kind of lake that makes you forget what day it is.
          </p>
          <p className="mt-3 text-white/60 text-sm">
            Photo by Jeff McCrea via{" "}
            <a
              href="https://www.pexels.com/photo/shore-of-lake-erie-by-mohawk-island-in-canada-19816324/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white/80"
            >
              Pexels
            </a>
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
                What Is Included
              </p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                A Proper BnB Stay Near Bay Beach
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Every room at Malak BnB Resort comes with the things that make a stay feel complete
                rather than just convenient. Breakfast is ready each morning. The pool is heated.
                The Wi-Fi works. Parking is free. It is a family-run place, and it shows in the
                details.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {INCLUDED.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 bg-background rounded-xl p-4 border border-border/50"
                  >
                    <item.icon className="h-5 w-5 text-primary shrink-0" />
                    <span className="text-sm font-medium text-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
                <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 shrink-0" />
                <span>Praised by guests for hospitality, cleanliness, and breakfast</span>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/gallery-pool-outdoor.webp"
                alt="Outdoor heated pool at Malak BnB Resort in Crystal Beach"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Rooms snapshot */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              The Rooms
            </p>
            <h2 className="text-3xl font-heading text-foreground">
              Four Rooms, All of Them Comfortable
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Hardwood floors, plush bedding, flat screen TV, air conditioning, in-room safe, and
              free Wi-Fi in every room. Rates start from C$345 per night and vary by season.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: "2 Double Beds",
                price: "From C$345",
                size: "450 sq ft",
                img: "/images/room-double-beds.webp",
                alt: "Two double beds room at Malak BnB Resort Crystal Beach",
                href: "/2-double-beds-room-crystal-beach",
              },
              {
                name: "Standard Double Bed",
                price: "From C$345",
                size: "400 sq ft",
                img: "/images/room-standard-double.webp",
                alt: "Standard double bed room at Malak BnB Resort",
                href: "/rooms",
              },
              {
                name: "Deluxe Queen Bed",
                price: "From C$355",
                size: "400 sq ft",
                img: "/images/room-deluxe-queen.webp",
                alt: "Deluxe queen room with fireplace at Malak BnB Resort",
                href: "/deluxe-queen-room-crystal-beach",
              },
              {
                name: "Queen Bed Suite",
                price: "From C$380",
                size: "440 sq ft",
                img: "/images/room-queen-suite.webp",
                alt: "Queen bed suite with living area at Malak BnB Resort",
                href: "/queen-bed-suite-crystal-beach",
              },
            ].map((room) => (
              <Link
                key={room.name}
                to={room.href as any}
                className="group block rounded-2xl overflow-hidden bg-card shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={room.img}
                    alt={room.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base text-foreground">{room.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{room.size}</p>
                  <p className="text-sm text-primary font-medium mt-2">{room.price}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              View All Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* Nearby */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/beach-bay.webp"
                alt="Bay Beach at Crystal Beach, Ontario on Lake Erie"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
                The Location
              </p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                Crystal Beach is a Good Base for All of It
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                The town itself is worth slowing down for. Bay Beach is five minutes on foot. There
                are local restaurants, cafes, and bike-friendly streets. And if you want to push
                further, Niagara Falls is 30 minutes east, Buffalo is 15 minutes south across the
                Peace Bridge, and Fort Erie and Ridgeway are both within easy reach.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {NEARBY.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <item.icon className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-2">
                <Link
                  to="/explore"
                  className="inline-flex items-center text-primary font-semibold text-sm hover:underline"
                >
                  Explore Crystal Beach and the area →
                </Link>
                <Link
                  to="/fort-erie-bed-and-breakfast"
                  className="inline-flex items-center text-primary font-semibold text-sm hover:underline"
                >
                  See the full Fort Erie area guide →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Common Questions
            </p>
            <h2 className="text-3xl font-heading text-foreground">
              About Malak BnB Resort in Crystal Beach
            </h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">
            Crystal Beach, Ontario
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Ready to stay at a Crystal Beach bed and breakfast?
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the Malak team will personally confirm your stay. No
            third-party fees. No automated confirmation. Just a direct connection.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Request Your Stay
            </Link>
            <a
              href="tel:+12899298055"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
